import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Grid,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { getCardRewards } from '../../data/cardTranslations';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import CardImage from './CardImage';
import FreshnessBadge from './FreshnessBadge';
import FinMapleScoreBadge from '../FinMapleScore/FinMapleScoreBadge';
import FinMapleScoreDetail from '../FinMapleScore/FinMapleScoreDetail';
import { parseFee, parseRate, fmtCurrency, fmtPercent, getCostoRealAnual } from './helpers';

/* ── i18n labels ── */
const L = {
  es: {
    title: 'Detalle de tarjeta',
    closeAria: 'Cerrar detalle',
    costTitle: 'Costo Real Estimado - Año 1',
    costNote: 'Incluye cuota anual + intereses estimados con saldo promedio de $1,500 CAD',
    keyData: 'Datos clave',
    annualFee: 'Cuota anual',
    purchaseRate: 'Tasa de compra',
    cashAdvance: 'Tasa de adelanto',
    balanceTransfer: 'Tasa transferencia',
    foreignFee: 'Cargo moneda ext.',
    minIncome: 'Ingreso mínimo',
    notRequired: 'No requerido',
    rewards: 'Recompensas',
    insurance: 'Seguros incluidos',
    insMobile: 'Seguro dispositivos',
    insMedical: 'Seguro viaje médico',
    insRental: 'Seguro vehículo rental',
    insPurchase: 'Protección de compras',
    scoreBreakdown: 'FinMaple Score - Desglose',
    pros: 'Ventajas',
    cons: 'Desventajas',
    apply: 'Solicitar esta tarjeta',
    addCompare: 'Agregar a comparación',
    // Pros/Cons auto-generated
    newcomerFriendly: 'Apta para recién llegados',
    noFee: 'Sin cuota anual',
    lowFee: 'Cuota anual baja',
    competitiveRate: 'Tasa de interés competitiva',
    hasCashback: 'Ofrece cash back',
    hasPoints: 'Programa de puntos',
    hasTravelRewards: 'Recompensas de viaje',
    hasTravelMedical: 'Seguro médico de viaje incluido',
    hasRentalIns: 'Seguro de auto rental incluido',
    notNewcomer: 'No diseñada para newcomers',
    highFee: 'Cuota anual alta',
    highRate: 'Tasa de interés elevada',
    noRewards: 'Sin programa de recompensas',
    noTravelMedical: 'Sin seguro médico de viaje',
  },
  en: {
    title: 'Card details',
    closeAria: 'Close details',
    costTitle: 'Estimated Real Cost - Year 1',
    costNote: 'Includes annual fee + estimated interest on an average balance of $1,500 CAD',
    keyData: 'Key data',
    annualFee: 'Annual fee',
    purchaseRate: 'Purchase rate',
    cashAdvance: 'Cash advance rate',
    balanceTransfer: 'Balance transfer rate',
    foreignFee: 'Foreign currency fee',
    minIncome: 'Minimum income',
    notRequired: 'Not required',
    rewards: 'Rewards',
    insurance: 'Insurance included',
    insMobile: 'Device insurance',
    insMedical: 'Travel medical insurance',
    insRental: 'Rental vehicle insurance',
    insPurchase: 'Purchase protection',
    scoreBreakdown: 'FinMaple Score - Breakdown',
    pros: 'Advantages',
    cons: 'Disadvantages',
    apply: 'Apply for this card',
    addCompare: 'Add to comparison',
    newcomerFriendly: 'Newcomer friendly',
    noFee: 'No annual fee',
    lowFee: 'Low annual fee',
    competitiveRate: 'Competitive interest rate',
    hasCashback: 'Offers cash back',
    hasPoints: 'Points program',
    hasTravelRewards: 'Travel rewards',
    hasTravelMedical: 'Travel medical insurance included',
    hasRentalIns: 'Rental vehicle insurance included',
    notNewcomer: 'Not designed for newcomers',
    highFee: 'High annual fee',
    highRate: 'High interest rate',
    noRewards: 'No rewards program',
    noTravelMedical: 'No travel medical insurance',
  },
  fr: {
    title: 'Détails de la carte',
    closeAria: 'Fermer les détails',
    costTitle: 'Coût réel estimé - An 1',
    costNote: 'Inclut les frais annuels + intérêts estimés sur un solde moyen de 1 500 $ CAD',
    keyData: 'Données clés',
    annualFee: 'Frais annuels',
    purchaseRate: 'Taux d\'achat',
    cashAdvance: 'Taux d\'avance de fonds',
    balanceTransfer: 'Taux de transfert',
    foreignFee: 'Frais devise étrangère',
    minIncome: 'Revenu minimum',
    notRequired: 'Non requis',
    rewards: 'Récompenses',
    insurance: 'Assurances incluses',
    insMobile: 'Assurance appareils',
    insMedical: 'Assurance médicale voyage',
    insRental: 'Assurance véhicule de location',
    insPurchase: 'Protection des achats',
    scoreBreakdown: 'FinMaple Score - Détail',
    pros: 'Avantages',
    cons: 'Désavantages',
    apply: 'Demander cette carte',
    addCompare: 'Ajouter à la comparaison',
    newcomerFriendly: 'Adaptée aux nouveaux arrivants',
    noFee: 'Sans frais annuels',
    lowFee: 'Frais annuels bas',
    competitiveRate: 'Taux d\'intérêt compétitif',
    hasCashback: 'Offre des remises en argent',
    hasPoints: 'Programme de points',
    hasTravelRewards: 'Récompenses voyage',
    hasTravelMedical: 'Assurance médicale voyage incluse',
    hasRentalIns: 'Assurance véhicule de location incluse',
    notNewcomer: 'Non conçue pour les nouveaux arrivants',
    highFee: 'Frais annuels élevés',
    highRate: 'Taux d\'intérêt élevé',
    noRewards: 'Pas de programme de récompenses',
    noTravelMedical: 'Pas d\'assurance médicale voyage',
  },
};

/* ── Auto-generated Pros / Contras ────────────── */
const generateProsContras = (card, l) => {
  const pros = [];
  const contras = [];
  const fee = parseFee(card.annual_fee);
  const rate = parseRate(card.purchase_rate);

  // Pros
  if (card.newcomer_friendly) pros.push(l.newcomerFriendly);
  if (fee === 0) pros.push(l.noFee);
  else if (fee < 50) pros.push(l.lowFee);
  if (rate !== null && rate < 15) pros.push(l.competitiveRate);
  const rewards = (card.rewards || '').toLowerCase();
  if (rewards.includes('cash') || rewards.includes('%')) pros.push(l.hasCashback);
  if (rewards.includes('points') || rewards.includes('puntos')) pros.push(l.hasPoints);
  if (rewards.includes('miles') || rewards.includes('aeroplan')) pros.push(l.hasTravelRewards);
  if (card.insurance?.travel_medical && card.insurance.travel_medical !== 'Not available') pros.push(l.hasTravelMedical);
  if (card.insurance?.rental_vehicle && card.insurance.rental_vehicle !== 'Not available') pros.push(l.hasRentalIns);

  // Contras
  if (!card.newcomer_friendly) contras.push(l.notNewcomer);
  if (fee >= 120) contras.push(l.highFee);
  if (rate !== null && rate >= 22) contras.push(l.highRate);
  if (!rewards || rewards.includes('not available') || rewards === 'no rewards') contras.push(l.noRewards);
  if (!card.insurance?.travel_medical || card.insurance.travel_medical === 'Not available') contras.push(l.noTravelMedical);

  return { pros: pros.slice(0, 4), contras: contras.slice(0, 3) };
};

/* ── Insurance row ────────────────────────────── */
const InsuranceRow = ({ label, value }) => {
  const has = value && value !== 'Not available';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
      {has ? (
        <CheckCircleIcon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
      ) : (
        <CancelIcon sx={{ fontSize: '1.1rem', color: 'text.disabled' }} />
      )}
    </Box>
  );
};

/* ── Stat item ────────────────────────────────── */
const StatItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
      {value}
    </Typography>
  </Box>
);

/* ── Main component ───────────────────────────── */
const CardDetailModal = ({ open, onClose, card, selectedForCompare = [], onToggleCompare, disclosure = {} }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  const l = L[lang] || L.es;

  if (!card) return null;

  const costoReal = getCostoRealAnual(card);
  const { pros, contras } = generateProsContras(card, l);
  const isInCompare = selectedForCompare.some((c) => c.id === card.id);
  const canAddToCompare = !isInCompare && selectedForCompare.length < 4;

  const income = card.min_income_personal;
  const incomeDisplay = (!income || income === 'N/A' || income.toLowerCase().includes('not required'))
    ? l.notRequired
    : (() => { const m = String(income).match(/([\d,]+)/); return m ? `$${m[1]} CAD` : income; })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '14px' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{l.title}</Typography>
        <IconButton onClick={onClose} aria-label={l.closeAria}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header: Image + Name + Score */}
        <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, mb: 3, alignItems: 'flex-start' }}>
          <CardImage card={card} width={fullScreen ? 100 : 140} height={fullScreen ? 64 : 90} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{card.bank}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
              {card.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <FinMapleScoreBadge card={card} />
              {card.v2?.freshness && <FreshnessBadge status={card.v2.freshness} />}
            </Box>
            {card.v2?.welcome_bonus && (
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, mt: 0.5, display: 'block' }}>
                {card.v2.welcome_bonus.headline}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Costo Real Año 1 */}
        <Box
          sx={{
            bgcolor: 'grey.50',
            borderRadius: '12px',
            p: 3,
            mb: 4,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
            {l.costTitle}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, fontFeatureSettings: "'tnum'", color: costoReal > 200 ? 'error.main' : 'primary.main', fontSize: { xs: '2rem', sm: '3rem' } }}>
            ${costoReal} CAD
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1, display: 'block' }}>
            {l.costNote}
          </Typography>
        </Box>

        {/* Datos clave */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
          {l.keyData}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <StatItem label={l.annualFee} value={fmtCurrency(card.annual_fee, lang)} />
          </Grid>
          <Grid item xs={6}>
            <StatItem label={l.purchaseRate} value={fmtPercent(card.purchase_rate)} />
          </Grid>
          <Grid item xs={6}>
            <StatItem label={l.cashAdvance} value={fmtPercent(card.cash_advance_rate)} />
          </Grid>
          <Grid item xs={6}>
            <StatItem label={l.balanceTransfer} value={fmtPercent(card.balance_transfer_rate)} />
          </Grid>
          <Grid item xs={6}>
            <StatItem label={l.foreignFee} value={card.foreign_conversion_fee || 'N/D'} />
          </Grid>
          <Grid item xs={6}>
            <StatItem label={l.minIncome} value={incomeDisplay} />
          </Grid>
        </Grid>

        {/* Recompensas */}
        {card.rewards && !card.rewards.toLowerCase().includes('not available') && card.rewards.toLowerCase() !== 'no rewards' && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
              {l.rewards}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              {getCardRewards(card.id, card.rewards, lang)}
            </Typography>
          </>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Seguros incluidos */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
          {l.insurance}
        </Typography>
        <Box sx={{ mb: 3 }}>
          <InsuranceRow label={l.insMobile} value={card.insurance?.mobile_device} />
          <InsuranceRow label={l.insMedical} value={card.insurance?.travel_medical} />
          <InsuranceRow label={l.insRental} value={card.insurance?.rental_vehicle} />
          <InsuranceRow label={l.insPurchase} value={card.insurance?.purchase_protection} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* FinMaple Score breakdown */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
          {l.scoreBreakdown}
        </Typography>
        <Box sx={{ mb: 3 }}>
          <FinMapleScoreDetail card={card} />
        </Box>

        {/* Pros / Contras */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {l.pros}
            </Typography>
            {pros.map((p) => (
              <Box key={p} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: '1rem', color: 'primary.main', mt: 0.3 }} />
                <Typography variant="body2">{p}</Typography>
              </Box>
            ))}
            {pros.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>-</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main', mb: 1 }}>
              {l.cons}
            </Typography>
            {contras.map((c) => (
              <Box key={c} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                <CancelIcon sx={{ fontSize: '1rem', color: 'error.main', mt: 0.3 }} />
                <Typography variant="body2">{c}</Typography>
              </Box>
            ))}
            {contras.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.disabled' }}>-</Typography>
            )}
          </Grid>
        </Grid>

        {/* CTAs */}
        <Button
          variant="contained"
          fullWidth
          href={card.affiliate_link || '#'}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            bgcolor: 'secondary.main',
            color: '#fff',
            fontWeight: 700,
            py: 1.5,
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: '1rem',
            mb: 1.5,
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          {l.apply}
        </Button>

        {canAddToCompare && (
          <Button
            variant="outlined"
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => onToggleCompare(card)}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 600,
              py: 1,
              borderRadius: '12px',
              textTransform: 'none',
            }}
          >
            {l.addCompare}
          </Button>
        )}

        {/* Disclosure microline */}
        {card.v2?.commercial?.disclosure_key && disclosure[card.v2.commercial.disclosure_key] && (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem', mt: 2, display: 'block' }}>
            {disclosure[card.v2.commercial.disclosure_key]}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CardDetailModal;
